import { useQuery } from '@tanstack/react-query';
import { logExceptionOnce } from '@hungryroot/o11y';
import {
  featureFlagNames,
  flagDefinitions,
  type FeatureFlags,
  type FeatureFlagValueType,
} from '../data/feature-flags';
import { fetchFeatureFlagDefinitions } from '../data/fetch-feature-flag-definitions';
import { resolveUserFeatureFlags } from '../utils/resolve-feature-flags/resolve-feature-flags';

type Options = {
  enabled?: boolean;
};

const MISSING_DYNAMIC_CONFIG_MESSAGE = 'Missing DynamicConfig';

function useFeatureFlagDefinitions(options: Options = {}) {
  const { enabled } = options;
  return useQuery({
    queryKey: ['feature-flags', 'definitions', featureFlagNames],
    queryFn: async () => {
      const result = await fetchFeatureFlagDefinitions(featureFlagNames);

      const { status, data } = result;
      if (status !== 200) {
        throw data;
      }

      return data;
    },
    gcTime: 15 * 60 * 1000,
    staleTime: 15 * 60 * 1000,
    retry: 0,
    enabled,
  });
}

// Dev note: this is tested in jest/src/tests/feature-flags/useFeatureFlag.spec.tsx
// due to a circular dependency with @hungryroot/mock-server
export function useFeatureFlag<T extends FeatureFlags>(
  flagName: T,
  options: Options = {}
): FeatureFlagValueType<T> | undefined | false {
  const defsQuery = useFeatureFlagDefinitions(options);
  const { data: defs, error } = defsQuery;

  // gracefully turn the feature off
  if (error !== null) {
    return false;
  }

  if (defs === undefined) {
    return undefined;
  }

  const serverMatch = defs.results.find((serverFlag) => {
    return serverFlag.name === flagName;
  });

  // Uses server value if it exists, otherwise uses default value
  const value = resolveUserFeatureFlags(
    flagName,
    flagDefinitions[flagName].schema,
    serverMatch?.value
  );

  if (
    serverMatch === undefined &&
    process.env.PUBLIC_ENABLE_MISSING_DYNAMIC_CONFIG_LOGS === 'true'
  ) {
    logExceptionOnce(MISSING_DYNAMIC_CONFIG_MESSAGE, {
      key: 'useFeatureFlag.missingDynamicConfig',
      flagName,
    });
  }

  return value;
}
